import { useMutation } from '@tanstack/react-query'
import {
  LakehouseConnectionInput,
  LakehouseConnectionTypeEnum,
  LakehouseTableStatusEnum,
  Uni,
} from '@vendia/management-api-types'
import clsx from 'clsx'
import dayjs from 'dayjs'
import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import Button from 'src/components/buttons/button'
import VendiaMenuButton from 'src/components/buttons/menu-button'
import Card from 'src/components/containers/card'
import Icon from 'src/components/icons/icon'
import Alert from 'src/components/messages/alert'
import HoverableTooltip from 'src/components/messages/hoverable-tooltip'
import ConfirmationModal from 'src/components/modals/confirmation-modal'
import Pill from 'src/components/pills/pill'
import TableComponent from 'src/components/tables/table'
import { Table } from 'src/types/types'
import useControlPlaneApi from 'src/utils/hooks/use-control-plane-api'
import { useGetLakehouseData } from 'src/utils/hooks/use-get-lakehouse-data'
import { TaskRun, useListTaskRuns } from 'src/utils/hooks/use-list-task-runs'
import { useRequestTaskRun } from 'src/utils/hooks/use-request-task-run'
import { getSourceTableInfo } from 'src/utils/lakehouse/tables'
import notify from 'src/utils/notify'
import { DeleteLakehouseTableDocument } from '@vendia/control-plane-api'

export const ComponentLakehouseTable = ({
  uni,
  nodeName,
  connections,
}: {
  uni: Uni
  nodeName: string
  connections: LakehouseConnectionInput[]
}) => {
  const [selectedTable, setSelectedTable] = useState<Table | null>(null)
  const [showSyncModal, setShowSyncModal] = useState<boolean>(false)
  const [showDeleteModal, setShowDeleteModal] = useState<boolean>(false)
  const { data, refetch } = useGetLakehouseData()
  const client = useControlPlaneApi()

  const tables = data?.tables ?? []

  const requestTaskRunMutation = useRequestTaskRun()

  const deleteLakehouseTableMutation = useMutation({
    mutationFn: ({ uniName, nodeName, vendiaTableId }: { uniName: string; nodeName: string; vendiaTableId: string }) =>
      client.graphqlFetch({ query: DeleteLakehouseTableDocument, variables: { input: { uniName, nodeName, vendiaTableId } } }),

    onError: (error) => notify.error(`Error deleting table: ${error}`),
    onSuccess: (response) => {
      if (response.errors) {
        notify.error(`${response.errors[0].message}`)
        return
      }
      if (!response.deleteLakehouseTable) {
        notify.error('Error deleting table')
        return
      }

      refetch()
      setShowDeleteModal(false)
      notify.success(`Successfully deleted table: ${selectedTable?.name}`)
    },
  })

  const NameCell = ({ row }: { row: any }) => {
    const table = row.original as Table
    const sourceInfo = getSourceTableInfo(table)
    const isOwnedTable = table._owner === nodeName
    const isShared = table._acl.length > 0
    return (
      <div className='flex w-full items-center gap-5'>
        <div className='shrink-0'>
          {!isOwnedTable || sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Vendia ? (
            <img
              src='/images/vendor/vendia-primary.png'
              alt='Vendia logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Snowflake ? (
            <img
              src='/images/vendor/snowflake-primary.png'
              alt='Snowflake logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Cloudera ? (
            <img
              src='/images/vendor/cloudera-primary.png'
              alt='Cloudera logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Databricks ? (
            <img
              src='/images/vendor/databricks-primary.png'
              alt='Databricks logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Postgres ? (
            <img
              src='/images/vendor/postgres-primary.png'
              alt='Postgres logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Upload ? (
            <img
              src='/images/vendor/upload-primary.png'
              alt='File Upload logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.S3 ? (
            <img
              src='/images/vendor/s3-primary.png'
              alt='S3 logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Mysql ? (
            <img
              src='/images/vendor/mysql-primary.png'
              alt='MySQL logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Bigquery ? (
            <img
              src='/images/vendor/bigquery-primary.png'
              alt='BigQuery logo'
              className='size-10 rounded-lg border border-slate-200'
            />
          ) : null}
        </div>
        <div className='space-y flex-1 overflow-hidden'>
          <div className={clsx('text-link w-full truncate text-lg font-semibold')}>{table.name}</div>
          <div className='text-light flex items-center gap-2 text-sm'>
            <Icon name={isShared ? 'share-2' : 'lock'} size={16} />
            {table._owner}
          </div>
        </div>
      </div>
    )
  }

  const SourceCell = ({ row }: { row: any }) => {
    const table = row.original as Table
    const isOwnedTable = table._owner === nodeName
    const sourceInfo = getSourceTableInfo(table)

    const VendiaSource = () => {
      const vendiaTableNames: string[] = []
      if (sourceInfo?.tableName) {
        const tableName = sourceInfo.tableName.split('.')[1] || sourceInfo.tableName
        vendiaTableNames.push(tableName)
      }
      if (sourceInfo?.joinQuery?.from) {
        sourceInfo.joinQuery.from.forEach((joinQuery: any) => {
          vendiaTableNames.push(joinQuery.table)
        })
      }
      const vendiaTables = tables.filter((t) => vendiaTableNames.some((vendiaTableName) => vendiaTableName === t.name))

      return (
        <div className={clsx('text-light')}>
          <div className='flex flex-col'>
            {vendiaTables.map((vendiaTable: Table) => (
              <div className='flex items-center gap-2' key={vendiaTable._id}>
                <img src='/images/vendor/vendia-secondary.png' alt='Vendia logo' className='size-4' />
                <Link className='truncate' to={`../data/tables/${vendiaTable?._id}`} title={vendiaTable?.name}>
                  {vendiaTable?.name}
                </Link>
              </div>
            ))}
          </div>
        </div>
      )
    }
    return (
      <div className='flex flex-col gap-2'>
        {isOwnedTable && (
          <>
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Snowflake && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/snowflake-secondary.png' alt='Snowflake logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Cloudera && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/cloudera-secondary.png' alt='Cloudera logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Postgres && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/postgres-secondary.png' alt='Postgres logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Upload && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/upload-secondary.png' alt='File Upload logo' className='size-4' />
                <div className='truncate'>{sourceInfo.fileName || sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.S3 && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/s3-secondary.png' alt='S3 logo' className='size-4' />
                <div className='truncate'>
                  <div className='text-neutral-8 max-w-[300px] truncate text-xs' title={sourceInfo.fileName}>
                    {sourceInfo.fileName?.split('/').pop()}
                  </div>
                </div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Mysql && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/mysql-secondary.png' alt='MySQL logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Bigquery && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/bigquery-secondary.png' alt='BigQuery logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Databricks && (
              <div className='text-light flex items-center gap-2'>
                <img src='/images/vendor/databricks-secondary.png' alt='Databricks logo' className='size-4' />
                <div className='truncate'>{sourceInfo.tableName}</div>
              </div>
            )}
            {sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Vendia && <VendiaSource />}
          </>
        )}
      </div>
    )
  }

  // Shared hook for task runs that can be used by multiple cells
  const useTableTaskRuns = (tableId: string) => {
    const taskRunsQuery = useListTaskRuns({
      typeFilter: 'TABLE',
      tableId: tableId,
    })
    return {
      taskRunsQuery,
      taskRuns: taskRunsQuery.data?.taskRuns ?? [],
    }
  }

  const SyncCell = ({ row }: { row: any }) => {
    const table = row.original as Table
    const isOwnedTable = table._owner === nodeName

    // Use shared task runs hook
    const { taskRunsQuery, taskRuns } = useTableTaskRuns(table._id)

    const lastSync = taskRuns?.[0]
    const isLastSyncRunning = lastSync?.status === 'RUNNING'

    const isLastSyncSuccess = !isLastSyncRunning && lastSync?.status === 'SUCCEEDED'

    let lastSuccessfulSync: any = lastSync
    if (!isLastSyncSuccess) {
      lastSuccessfulSync = taskRuns?.find((taskRun: TaskRun) => {
        return taskRun.status === 'SUCCEEDED'
      })
    }

    const lastSuccessfulSyncTime = lastSuccessfulSync ? lastSuccessfulSync.endTime : null

    const syncfailureTime = lastSync && !isLastSyncSuccess ? lastSync.endTime : null

    const parentTables = useMemo(() => {
      const sourceInfo = getSourceTableInfo(table)
      // Check for join query parent tables
      if (sourceInfo?.joinQuery?.from?.length > 0) {
        return sourceInfo?.joinQuery.from
      }

      // Check for single Vendia table parent
      if (sourceInfo?.connectionType === LakehouseConnectionTypeEnum.Vendia) {
        return [{ table: sourceInfo.tableName }]
      }
      return null
    }, [table])

    // Fetch task runs for all parent tables
    const parentTableQueries = useMemo(() => {
      if (!parentTables) return []
      return parentTables
        .map((parentTableRef: any) => {
          const parentTable = tables.find((t) => t.name === parentTableRef.table)
          return parentTable ? parentTable._id : null
        })
        .filter(Boolean)
    }, [parentTables, tables])

    // Create individual queries for each parent table - only fetch successful runs
    const parentTaskRunQueries = parentTableQueries.map((parentTableId: any) =>
      useListTaskRuns({
        typeFilter: 'TABLE',
        tableId: parentTableId,
        statusFilter: 'SUCCEEDED',
        enabled: !!parentTableId,
      }),
    )

    const parentTablesOutOfSync = () => {
      if (!parentTables || !lastSuccessfulSyncTime) return false

      const outOfSyncParents = []

      for (let i = 0; i < parentTables.length; i++) {
        const parentTableRef = parentTables[i]
        const parentTable = tables.find((t) => t.name === parentTableRef.table)
        if (!parentTable) continue

        const parentTaskRunQuery = parentTaskRunQueries[i]
        const parentTaskRuns = parentTaskRunQuery?.data?.taskRuns ?? []

        if (!parentTaskRuns || parentTaskRuns.length === 0) continue

        const parentLastSuccessfulSync = parentTaskRuns[0]
        const parentLastSuccessfulSyncTime = parentLastSuccessfulSync.endTime

        if (new Date(parentLastSuccessfulSyncTime) > new Date(lastSuccessfulSyncTime)) {
          outOfSyncParents.push(parentTableRef)
        }
      }

      return outOfSyncParents.length > 0 ? outOfSyncParents : false
    }

    const outOfSyncParents = parentTablesOutOfSync()
    const isOutOfSync = !!outOfSyncParents

    const getTableState = () => {
      if (taskRunsQuery.isLoading && isOwnedTable) return 'loading'
      if (lastSync && isLastSyncRunning) return 'syncing'
      if (lastSync && !isLastSyncRunning && isLastSyncSuccess) return 'success'
      if (lastSync && !isLastSyncRunning && !isLastSyncSuccess) return 'failed'
      return 'syncing' // Default to syncing if no other state matches, because the async nature of task runs
    }

    const tableState = getTableState()

    const renderTableStatus = () => {
      switch (tableState) {
        case 'loading':
          return <Pill className='animate-pulse' text='Loading...' />

        case 'syncing':
          return (
            <HoverableTooltip
              align='start'
              content={
                <div className='flex max-w-xl flex-col gap-1'>
                  <div className='mb-2 font-semibold'>Sync progress</div>
                  <div className='flex justify-between gap-8'>
                    <div>Syncing latest data from source:</div>
                    {lastSync?.status === 'RUNNING' ? (
                      <span className='text-information-10 font-semibold'>Syncing...</span>
                    ) : (
                      <span className='text-success-13 font-semibold'>Complete!</span>
                    )}
                  </div>
                </div>
              }
              side='bottom'
              sideOffset={10}
              isModern
            >
              <Pill kind='info' className='animate-pulse' text='Syncing...' />
            </HoverableTooltip>
          )

        case 'success':
          return (
            <HoverableTooltip
              align='start'
              content={
                <span className='font-semibold'>
                  Sync succeeded on {dayjs(lastSuccessfulSyncTime).toDate().toLocaleString()}
                </span>
              }
              side='bottom'
              sideOffset={10}
              isModern
            >
              <Pill kind='success' className='flex items-center gap-1'>
                <Icon name='sync' size={16} />
                {dayjs(lastSuccessfulSyncTime).fromNow()}
              </Pill>
            </HoverableTooltip>
          )

        case 'failed':
          return (
            <HoverableTooltip
              align='start'
              content={
                <div className='flex max-w-xl flex-col gap-1 text-wrap'>
                  {syncfailureTime ? (
                    <div className='mb-2 font-semibold'>Sync failed {dayjs(syncfailureTime).fromNow()}</div>
                  ) : null}
                  {(lastSync.errorMessage || lastSync.status === 'FAILED') && (
                    <div>
                      {/* we could parse errors from source.error and show certain messages if any prove to be useful */}
                      An error occurred during our attempt to pull from your data source.
                    </div>
                  )}
                </div>
              }
              side='bottom'
              sideOffset={10}
              isModern
            >
              <div className='text-error-8'>
                {(lastSync.errorMessage || lastSync.status === 'FAILED') && (
                  <Pill kind='warning' className='flex items-center gap-1'>
                    <Icon name='x' size={16} />
                    {dayjs(syncfailureTime).fromNow()}
                  </Pill>
                )}
              </div>
            </HoverableTooltip>
          )

        default:
          return null
      }
    }

    return (
      <div className='text-dark flex flex-col gap-1 text-sm'>
        {renderTableStatus()}
        {/* Out of sync with parent(s) - shown independently of other states */}
        {isOutOfSync && (
          <HoverableTooltip
            align='start'
            content={
              <div className='flex max-w-xl flex-col gap-1 text-wrap'>
                <div>
                  Parent table{outOfSyncParents.length > 1 ? 's' : ''} (
                  {outOfSyncParents.map((p) => p.table).join(', ')}){outOfSyncParents.length > 1 ? ' have' : ' has'}{' '}
                  been updated since this table was last synced.
                </div>
              </div>
            }
            side='bottom'
            sideOffset={10}
            isModern
          >
            <Pill kind='pending' className='flex items-center gap-1'>
              <Icon name='workflow' size={16} />
              Upstream changed
            </Pill>
          </HoverableTooltip>
        )}
      </div>
    )
  }

  const ActionsCell = ({ row }: { row: any }) => {
    const table = row.original as Table
    const isTableReady = table.status === LakehouseTableStatusEnum.Ready || !table.status
    const isOwnedTable = table._owner === nodeName

    const { taskRuns } = useTableTaskRuns(table._id)

    return (
      <div className='flex items-center justify-end gap-2'>
        <Button
          kind='icon'
          size='small'
          icon='sync'
          label='Sync table'
          onClick={() => {
            setSelectedTable(table)
            setShowSyncModal(true)
          }}
          disabled={!isTableReady || taskRuns?.[0]?.status === 'RUNNING' || !isOwnedTable}
        />
        <div className='pl-3'>
          <VendiaMenuButton
            menuItems={[
              <Button
                key={'delete'}
                kind='danger'
                onClick={() => {
                  // Quick low-effort hack TODO: implement this properly
                  setSelectedTable(table)
                  setShowDeleteModal(true)
                }}
                className='w-full !justify-start'
                disabled={!isOwnedTable}
              >
                Delete table
              </Button>,
            ]}
          >
            <Icon name='ellipsis' size='xs' aria-label='Open Actions' />
          </VendiaMenuButton>
        </div>
      </div>
    )
  }

  return (
    <Card overflow='visible' isTable contentExpandVertical>
      <TableComponent
        className='table-fixed'
        columns={[
          {
            accessorKey: 'name',
            header: 'Table',
            cell: NameCell,
            enableSorting: false,
            meta: {
              className: 'w-2/5',
            },
          },
          {
            accessorKey: 'source',
            header: 'Source',
            cell: SourceCell,
            enableSorting: false,
          },
          {
            accessorKey: 'status',
            header: 'Sync Status',
            cell: SyncCell,
            enableSorting: false,
          },
          {
            accessorKey: 'actions-column',
            header: 'Actions',
            cell: ActionsCell,
            enableSorting: false,
            meta: {
              className: 'text-right',
            },
          },
        ]}
        data={tables}
        isRowLinkEnabled={({ cell }: any) => {
          if (['actions-column', 'source'].includes(cell?.column?.id)) {
            return false
          }
          return true
        }}
        getRowLinkTo={(row: any) => {
          return {
            to: `../data/tables/${row.original._id}`,
            state: { search: location.search },
          }
        }}
        rowDividers={true}
      />
      <ConfirmationModal // Sync modal
        isOpen={showSyncModal}
        onClose={() => setShowSyncModal(false)}
        title={`Confirm sync for ${selectedTable?.name}`}
        onSubmit={() =>
          requestTaskRunMutation.mutate(
            { tableId: selectedTable?._id! },
            {
              onSuccess: (result) => {
                if (result?.isRequested) {
                  notify.success(`Successfully initiated sync for: ${selectedTable?.name}`)
                  setShowSyncModal(false)
                  setTimeout(() => {
                    refetch()
                  }, 6000) // task run creation is async
                } else {
                  notify.error('Failed to request task run')
                }
              },
              onError: (error) => {
                notify.error(`Error syncing table: ${error.message}`)
              },
            },
          )
        }
        isSubmitting={requestTaskRunMutation.isPending}
        actionButtonType={`primary`}
        actionButtonText={`Sync`}
        confirmationText={'sync'}
      >
        <div className='grid gap-4'>
          <p>
            Immediately begin syncing the latest data from your configured source to{' '}
            <strong>{selectedTable?.name}</strong>
          </p>
          <Alert kind='info'>Note: Once started, the sync process cannot be cancelled or undone.</Alert>
        </div>
      </ConfirmationModal>
      <ConfirmationModal // Delete modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title={`Confirm deletion of ${selectedTable?.name}`}
        onSubmit={() =>
          deleteLakehouseTableMutation.mutate({
            uniName: uni.name,
            nodeName: nodeName,
            vendiaTableId: selectedTable?._id!,
          })
        }
        isSubmitting={deleteLakehouseTableMutation.isPending}
        actionButtonText={`Delete`}
        confirmationText={selectedTable?.name}
        fullConfirmationPrompt={`Type ${selectedTable?.name} to confirm`}
        fullConfirmationPlaceholder={`Type ${selectedTable?.name} to confirm`}
      >
        <div className='grid gap-4'>
          <p className='text-sm text-gray-500'>Are you sure you want to delete {selectedTable?.name}?</p>
          <p className='text-sm text-gray-500'>This action cannot be undone.</p>
        </div>
      </ConfirmationModal>
    </Card>
  )
}
